import { RouterProvider } from 'react-router-dom';
import { QueryClientProvider } from 'react-query';
import { Toaster } from 'react-hot-toast';
import { queryClient } from './config/queryClient';
import { AuthProvider } from './contexts/AuthContext';
import { ErrorProvider, useErrorModal } from './contexts/ErrorContext';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { ToastProvider } from './components/common/Toast';
import { ThemeProvider } from './contexts/ThemeContext';
import { AdminProvider } from './contexts/AdminContext';
import ErrorModal from './components/common/ErrorModal';
import { router } from './routes';
import { getPortalType, getSubdomain } from './config/domain';
import { usePortalInfo } from './hooks/usePortal';
import PortalNotFound from './pages/PortalNotFound';
import './config/amplify';

/** Gate that validates company subdomains before rendering the app */
function PortalGate({ children }: { children: React.ReactNode }) {
  const portalType = getPortalType();
  const subdomain = getSubdomain();

  // Only validate for company-type subdomains
  const isCompanyPortal = portalType === 'company';
  const { data, isLoading, isError } = usePortalInfo(isCompanyPortal ? subdomain : null);

  if (isCompanyPortal) {
    if (isLoading) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      );
    }
    if (isError || !data) {
      return <PortalNotFound />;
    }
  }

  return <>{children}</>;
}

function AppContent() {
  const { errorModal, closeErrorModal } = useErrorModal();

  return (
    <>
      <RouterProvider router={router} />
      <Toaster />
      <ErrorModal
        isOpen={errorModal.isOpen}
        onClose={closeErrorModal}
        title={errorModal.title}
        message={errorModal.message}
        traceId={errorModal.traceId}
      />
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <ErrorProvider>
            <PortalGate>
              <AuthProvider>
                <AdminProvider>
                  <ThemeProvider>
                    <AppContent />
                  </ThemeProvider>
                </AdminProvider>
              </AuthProvider>
            </PortalGate>
          </ErrorProvider>
        </ToastProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
